// services/organization.service.js
import httpClient from './httpClient';
import TokenService from './token.service';

/**
 * Organization Service
 * Maneja todas las operaciones relacionadas con Organizations
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

const OrganizationService = {
  /**
   * Crear nueva organización
   * POST /organizations
   */
  async createOrganization(organizationData) {
    try {
      const response = await authClient.post('/organizations', organizationData);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('❌ Error creating organization:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to create organization']
      };
    }
  },

  /**
   * Obtener detalles de una organización
   * GET /organizations/{organization_id}
   * Este endpoint obtiene la información completa de una organización para cuando se selecciona en el switcher
   */
  async getOrganization(organizationId) {
    try {
      const response = await authClient.get(`/organizations/${organizationId}`);
      return {
        success: true,
        data: response.data.body // Devolver body completo, no solo organization
      };
    } catch (error) {
      console.error('❌ Error fetching organization:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to fetch organization']
      };
    }
  },

  /**
   * Actualizar organización
   * PUT /organizations/{organization_id}
   */
  async updateOrganization(organizationId, updates) {
    try {
      const response = await authClient.put(`/organizations/${organizationId}`, updates);
      return {
        success: true,
        data: response.data.body.organization
      };
    } catch (error) {
      console.error('❌ Error updating organization:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to update organization']
      };
    }
  },

  /**
   * Eliminar organización
   * DELETE /organizations/{organization_id}
   */
  async deleteOrganization(organizationId) {
    try {
      const response = await authClient.delete(`/organizations/${organizationId}`);
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error deleting organization:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to delete organization']
      };
    }
  },

  /**
   * Listar organizaciones del usuario
   * GET /organizations
   */
  async listOrganizations() {
    try {
      const response = await authClient.get('/organizations');
      return {
        success: true,
        data: response.data.body.organizations,
        totalCount: response.data.body.total_count
      };
    } catch (error) {
      // Silenciar 404 ya que el endpoint puede no estar implementado aún
      if (error.response?.status === 404) {
        console.warn('Organizations endpoint (GET /organizations) not implemented yet');
      } else {
        console.error('Error listing organizations:', error);
      }
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to list organizations'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Agregar miembro a organización
   * POST /organizations/{organization_id}/members
   * @param {string} organizationId - UUID de la organización
   * @param {Object} memberData - Datos del miembro
   * @param {string} memberData.user_id - UUID del usuario (requerido)
   * @param {string} [memberData.role='MEMBER'] - Rol del miembro (MEMBER, ADMIN, OWNER)
   * @param {string} [memberData.request_id] - ID de solicitud opcional
   */
  async addMember(organizationId, memberData) {
    try {
      const payload = {
        user_id: memberData.user_id,
        role: memberData.role || 'MEMBER'
      };
      if (memberData.request_id) {
        payload.request_id = memberData.request_id;
      }

      const response = await authClient.post(
        `/organizations/${organizationId}/members`,
        payload
      );
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error adding member:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to add member']
      };
    }
  },

  /**
   * Remover miembro de organización
   * DELETE /organizations/{organization_id}/members/{user_id}
   */
  async removeMember(organizationId, userId) {
    try {
      const response = await authClient.delete(
        `/organizations/${organizationId}/members/${userId}`
      );
      return {
        success: true,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error removing member:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to remove member']
      };
    }
  },

  /**
   * Actualizar rol de miembro
   * PUT /organizations/{organization_id}/members/{user_id}
   */
  async updateMemberRole(organizationId, userId, newRole, requestId = null) {
    try {
      const payload = { role: newRole };
      if (requestId) payload.request_id = requestId;
      
      const response = await authClient.put(
        `/organizations/${organizationId}/members/${userId}`,
        payload
      );
      return {
        success: true,
        data: response.data.body.membership
      };
    } catch (error) {
      console.error('Error updating member role:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to update member role']
      };
    }
  },

  /**
   * Listar miembros de organización
   * GET /organizations/{organization_id}/members
   * @param {string} organizationId - UUID de la organización
   * @param {Object} options - Opciones de filtrado
   * @param {boolean} [options.includeInactive=false] - Incluir miembros inactivos
   * @param {string} [options.roleFilter] - Filtrar por rol (MEMBER, ADMIN, OWNER)
   */
  async listMembers(organizationId, options = {}) {
    try {
      const params = {};
      if (options.includeInactive !== undefined) {
        params.include_inactive = options.includeInactive;
      }
      if (options.roleFilter) {
        params.role_filter = options.roleFilter;
      }

      const response = await authClient.get(
        `/organizations/${organizationId}/members`,
        { params }
      );
      return {
        success: true,
        data: response.data.body.members,
        totalCount: response.data.body.total_count
      };
    } catch (error) {
      console.error('Error listing members:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to list members'],
        data: [],
        totalCount: 0
      };
    }
  },

  /**
   * Buscar organizaciones
   * GET /organizations/search?term=...&active_only=...&limit=...&offset=...
   */
  async searchOrganizations({ 
    term, 
    activeOnly = true, 
    limit = 10, 
    offset = 0 
  } = {}) {
    try {
      const response = await authClient.get('/organizations/search', {
        params: {
          term,
          active_only: activeOnly,
          limit,
          offset
        }
      });
      return {
        success: true,
        data: response.data.body.organizations,
        totalCount: response.data.body.total_count,
        searchTerm: response.data.body.search_term
      };
    } catch (error) {
      console.error('Error searching organizations:', error);
      return {
        success: false,
        error: error.response?.data?.error || ['Failed to search organizations'],
        data: [],
        totalCount: 0
      };
    }
  }
};

export default OrganizationService;
